import { AnimatePresence, LayoutGroup, motion } from 'motion/react'
import { useEffect, useState } from 'react'

import { cn } from '@forest/shared'
import forestImg from '~/assets/forest.png'
import reward1Img from '~/assets/reward-1.png'
import reward2Img from '~/assets/reward-2.png'
import { FadeInButton } from '~/components/fade-in-button'
import { ArrowRight } from '~/components/graphics/arrow-right'
import { BigLight } from '~/components/graphics/big-light'
import { Bush } from '~/components/graphics/bush'
import { ForProUser } from '~/components/graphics/for-pro-user'
import { Shadow } from '~/components/graphics/shadow'
import { Sparkles } from '~/components/graphics/sparkles'
import { Sprout } from '~/components/graphics/sprout'
import { Sunlight } from '~/components/graphics/sunlight'
import { Page1Content } from '~/components/page-1-content'
import { Page2Content } from '~/components/page-2-content'
import { Page3Content } from '~/components/page-3-content'
import { Page4Content } from '~/components/page-4-content'
import { RewardContent } from '~/components/reward-content'
import { m } from '~/paraglide/messages.js'
import { BlurLighting } from './components/graphics/blur-lighting'
import { native } from '@forest/native-services'

/**
 * Reveal animation sequence (opacity fade-in, 1s each):
 *
 *  0.0s – 1.4s  ForProUser path-length animation (in for-pro-user.tsx)
 *  1.4s – 2.4s  Title "獻給專業版用戶的一封信"
 *  2.4s – 3.4s  Body text 1
 *  3.4s – 4.4s  Body text 2
 *  4.4s – 5.4s  Body text 3
 *  5.4s – 6.4s  Pro user info card
 *  6.4s – 7.4s  Arrow right button (in arrow-right.tsx), then infinite bounce
 */
const fade = { initial: { opacity: 0 }, animate: { opacity: 1 } }
const shadowFrameStyle = {
  top: '38.15%',
  right: '-124.87%',
  bottom: '-6.83%',
  left: '-61.31%',
} as const
const shadowOverflowStyle = {
  inset: '-23.327px',
} as const

export function App() {
  const [page, setPage] = useState<1 | 2 | 3 | 4>(1)
  const [reward, setReward] = useState<null | 1 | 2>(null)
  const [revealed, setRevealed] = useState(false)
  const [skipRevealNonce, setSkipRevealNonce] = useState(0)
  const [isPageContentRevealed, setIsPageContentRevealed] = useState(false)

  useEffect(() => {
    setIsPageContentRevealed(false)
  }, [page])

  return (
    <div
      className="relative flex h-full flex-col items-center overflow-hidden px-6 pb-12 pt-9"
      style={{
        background:
          'linear-gradient(to bottom, #037165 7.16%, #6be4bb 67.243%)',
      }}
      onClick={() => {
        if (reward !== null) return
        setSkipRevealNonce((value) => value + 1)
      }}
    >
      {/* Shadow layer */}
      <div className="pointer-events-none absolute" style={shadowFrameStyle}>
        <div className="absolute" style={shadowOverflowStyle}>
          <Shadow className="h-full w-full" />
        </div>
      </div>

      {/* Bush layer */}
      <div className="pointer-events-none absolute bottom-[-192px] left-[-167px] flex h-[508px] w-[933px] items-center justify-center">
        <Bush />
      </div>

      {/* Big light */}
      <div
        className="pointer-events-none absolute top-[98px] size-[324.663px]"
        style={{ left: 'calc(50% + 134.83px)', transform: 'translateX(-50%)' }}
      >
        <BigLight className="h-full w-full" />
      </div>

      {/* Sunlight rays */}
      <Sunlight className="pointer-events-none absolute left-1/2 top-0 h-[424px] w-[1155.243px] -translate-x-1/2 overflow-clip" />

      {/* Content */}
      <div
        className={cn(
          'relative flex min-h-0 w-full flex-1 flex-col items-center gap-9 pb-[60px]',
          page > 2 && 'pb-[60px]',
        )}
      >
        {/* Hero section */}
        <LayoutGroup>
          <motion.div
            layout="position"
            className={cn(
              'flex w-full flex-1 flex-col items-center justify-center gap-3',
            )}
          >
            {/* Sprout / reward preview */}
            <motion.div
              layout="position"
              transition={{ layout: { duration: 0.5, ease: 'easeInOut' } }}
              className="relative"
            >
              <AnimatePresence mode="wait">
                {page <= 2 ? (
                  <motion.div
                    key="sprout"
                    className="pointer-events-none relative h-[117.129px] w-[152.947px] shrink-0"
                    exit={{ opacity: 0, transition: { duration: 0.3 } }}
                  >
                    <div className="absolute inset-[-81.05%_-103.02%_-64.78%_-52.96%]">
                      <Sprout className="h-full w-full" />
                    </div>
                  </motion.div>
                ) : page === 3 ? (
                  <motion.div
                    className="relative shrink-0"
                    key="tree-preview"
                    initial={{ opacity: 0 }}
                    animate={revealed ? { opacity: 1 } : { opacity: 0 }}
                    exit={{ opacity: 0, transition: { duration: 0.3 } }}
                    transition={{ duration: 1 }}
                  >
                    <div
                      className="absolute bottom-[-38px] left-1/2 h-[73px] w-[200px] -translate-x-1/2 rounded-[50%]"
                      style={{
                        background:
                          'radial-gradient(85.37px 30.59px at center, #F6F396, rgba(255, 250, 96, 0))',
                        filter: 'blur(12px)',
                        willChange: 'filter',
                      }}
                    />
                    <img
                      src={reward1Img}
                      alt=""
                      className="relative h-[140px] object-contain"
                    />
                  </motion.div>
                ) : (
                  <motion.div
                    key="forest-preview"
                    className="relative shrink-0"
                    initial={{ opacity: 0 }}
                    animate={revealed ? { opacity: 1 } : { opacity: 0 }}
                    transition={{ duration: 1 }}
                  >
                    <div
                      className="absolute -bottom-4 left-1/2 h-[126px] w-[347px] -translate-x-1/2 rounded-[50%]"
                      style={{
                        background:
                          'radial-gradient(148.11px 53px at center, #F6F396, rgba(255, 250, 96, 0))',
                        filter: 'blur(13px)',
                        willChange: 'filter',
                      }}
                    />
                    <img
                      className="relative h-[178px] object-contain"
                      src={forestImg}
                      alt=""
                    />
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Blur lighting */}
              <div className="absolute left-1/2 top-1/2 h-[163px] w-[212px] -translate-x-1/2 -translate-y-1/2">
                <BlurLighting />
              </div>
            </motion.div>

            {/* Title */}
            <motion.div
              layout="position"
              transition={{ layout: { duration: 0.5, ease: 'easeInOut' } }}
              className="flex w-full flex-col items-center"
            >
              {page <= 2 && (
                <div className="relative h-[94px] w-full overflow-clip">
                  <div className="absolute left-1/2 top-[17px] h-[60px] w-[274px] -translate-x-1/2">
                    <div className="absolute inset-[-7.5%_-1.64%]">
                      <ForProUser className="h-full w-full" />
                    </div>
                  </div>
                </div>
              )}
              <AnimatePresence mode="wait">
                {page === 1 ? (
                  <motion.p
                    key="title-1"
                    className="text-center text-2xl font-bold text-white"
                    style={{
                      textShadow: '0px 0px 4.5px rgba(255, 255, 255, 0.73)',
                    }}
                    {...fade}
                    exit={{
                      opacity: 0,
                      transition: { duration: 0.3, delay: 0 },
                    }}
                    transition={{ duration: 1, delay: 1.4 }}
                  >
                    {m.pro_user_appreciation_letter_gratitude_title()}
                  </motion.p>
                ) : page === 2 ? (
                  <motion.p
                    key="title-2"
                    className="text-center text-2xl font-bold text-white"
                    style={{
                      textShadow: '0px 0px 4.5px rgba(255, 255, 255, 0.73)',
                    }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{
                      opacity: 0,
                      transition: { duration: 0.3, delay: 0 },
                    }}
                    transition={{ duration: 1, delay: 1 }}
                  >
                    {m.pro_user_appreciation_letter_commitment_title()}
                  </motion.p>
                ) : (
                  <p
                    key={page === 3 ? 'title-3' : 'title-4'}
                    className="text-center text-2xl font-bold text-white"
                    style={{
                      textShadow: '0px 0px 4.5px rgba(255, 255, 255, 0.73)',
                    }}
                  >
                    {page === 3
                      ? m.tree_type_159_title()
                      : m.land_skin_5_title()}
                  </p>
                )}
              </AnimatePresence>
            </motion.div>
          </motion.div>
        </LayoutGroup>

        {/* Body section */}
        <div
          className="relative flex w-full max-w-[640px] flex-1 flex-col items-center gap-3 overflow-y-auto px-4"
          key={page}
        >
          <AnimatePresence mode="wait">
            {page === 1 ? (
              <Page1Content
                key="body-1"
                skipNonce={skipRevealNonce}
                onRevealsComplete={() => setIsPageContentRevealed(true)}
              />
            ) : page === 2 ? (
              <Page2Content
                key="body-2"
                skipNonce={skipRevealNonce}
                onRevealsComplete={() => setIsPageContentRevealed(true)}
              />
            ) : page === 3 ? (
              <Page3Content
                key="body-3"
                revealed={revealed}
                skipNonce={skipRevealNonce}
                onRevealsComplete={() => setIsPageContentRevealed(true)}
              />
            ) : (
              <Page4Content
                key="body-4"
                revealed={revealed}
                skipNonce={skipRevealNonce}
                onRevealsComplete={() => setIsPageContentRevealed(true)}
              />
            )}
          </AnimatePresence>
        </div>

        {/* Arrow button — page 1 only */}
        <AnimatePresence>
          {page === 1 && isPageContentRevealed && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1 }}
              exit={{ opacity: 0, transition: { duration: 0.4 } }}
              className="fixed bottom-0 left-0 right-0 flex justify-center pb-12"
            >
              <ArrowRight
                className="relative size-[42px] shrink-0"
                delay={0}
                onClick={() => setPage(2)}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* "Open gift" button — page 2 only */}
        <AnimatePresence>
          {page === 2 && isPageContentRevealed && (
            <FadeInButton
              className="fixed bottom-12 left-6 right-6 mx-auto h-[52px] max-w-[640px] rounded-xl bg-white px-4 text-base font-bold text-[#176361] shadow-[0px_0px_20px_6px_rgba(198,255,205,0.14)]"
              exit={{ opacity: 0, transition: { duration: 0.3 } }}
              delay={0}
              onClick={() => {
                setReward(1)
                setTimeout(() => {
                  setPage(3)
                }, 1000)
              }}
            >
              {m.btn_open_gift()}
            </FadeInButton>
          )}
        </AnimatePresence>

        {/* "Open final easter egg" button — page 3 only */}
        {page === 3 && revealed && reward === null && isPageContentRevealed && (
          <FadeInButton
            className="fixed bottom-12 left-6 right-6 mx-auto h-[52px] max-w-[640px] rounded-xl bg-[#fffdb6] px-4 text-base font-bold text-[#176361]"
            delay={0}
            onClick={() => {
              setRevealed(false)
              setReward(2)
              setTimeout(() => {
                setPage(4)
              }, 1000)
            }}
          >
            {m.btn_open_easter_egg()}
          </FadeInButton>
        )}

        {/* "Go check it out" button — page 4 only */}
        <AnimatePresence>
          {page === 4 &&
            revealed &&
            reward === null &&
            isPageContentRevealed && (
              <FadeInButton
                className="fixed bottom-12 left-6 right-6 mx-auto h-[52px] max-w-[640px] rounded-xl bg-white px-4 text-base font-bold text-[#176361]"
                delay={0}
                onClick={() => {
                  native.general.close()
                }}
              >
                {m.btn_check_out_now()}
              </FadeInButton>
            )}
        </AnimatePresence>

        {/* Sparkles overlay */}
        <div className="pointer-events-none absolute inset-[44.43%_-2.13%_2.85%_1.27%]">
          <div className="absolute inset-[-2.19%_-2.54%]">
            <Sparkles className="h-full w-full" />
          </div>
        </div>
      </div>

      {/* Reward overlay */}
      <AnimatePresence>
        {reward === 1 && (
          <motion.div
            key="reward-1"
            className="fixed inset-0 z-50 flex flex-col"
            style={{
              background:
                'radial-gradient(125.2% 64.85% at 50.13% 44.75%, #BBF5CF 9.92%, #3FAB94 45%, #176361 100%)',
            }}
            initial={{ opacity: 0 }}
            animate={{
              opacity: 1,
              transition: { duration: 0.3, ease: [0.33, 1, 0.68, 1] },
            }}
            exit={{ opacity: 0, transition: { duration: 0.3 } }}
          >
            <RewardContent
              image={reward1Img}
              title={m.tree_type_159_title()}
              textColor="text-white"
              buttonLabel={m.claimed_next()}
              onButtonClick={() => {
                setReward(null)
                setRevealed(true)
              }}
            />
          </motion.div>
        )}
        {reward === 2 && (
          <motion.div
            key="reward-2"
            className="fixed inset-0 z-50 flex flex-col"
            style={{
              background:
                'radial-gradient(116.79% 53.87% at 44.78% 46.13%, #FAFFE6 0%, #F6F396 93.16%)',
            }}
            initial={{ opacity: 0 }}
            animate={{
              opacity: 1,
              transition: { duration: 0.3, ease: [0.33, 1, 0.68, 1] },
            }}
            exit={{ opacity: 0, transition: { duration: 0.3 } }}
          >
            <RewardContent
              image={reward2Img}
              title={m.land_skin_5_title()}
              description={m.forest_theme()}
              textColor="text-[#176361]"
              buttonLabel={m.claimed_next()}
              onButtonClick={() => {
                setReward(null)
                setRevealed(true)
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
